/* =============================================================
   PROMPTVAULT — background animation scheduler
   One shared, throttled + pausable rAF loop for the full-screen
   "matrix rain" canvases on the landing + course pages.

   Why: each page painted a viewport-sized canvas every frame
   (~60fps), several with mix-blend-mode:screen, behind a sticky
   backdrop-filter header. That repaint competed with the
   compositor on every scroll frame, and kept running (blurred)
   under the open cart drawer — the main cause of the jank.

   pvBgLoop(drawFrame) drives drawFrame at ~30fps and skips it
   entirely when:
     - the tab is hidden (rAF is suspended anyway),
     - the cart drawer is open (<html class="pv-bg-off">),
     - the visitor prefers reduced motion (one static frame, then stop).
   Returns { stop } so a re-init (e.g. language switch) can cancel
   the previous loop cleanly.

   Loaded early (deferred, in <head>) so it's defined before any
   deferred page script that calls it.
   ============================================================= */
(function () {
  'use strict';
  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var FRAME_MS = 1000 / 30;

  function paused() {
    return document.hidden || document.documentElement.classList.contains('pv-bg-off');
  }

  /* ---------------------------------------------------------------
     pvOnResize(fn) — one debounced resize channel for every canvas.

     Each page used to do `addEventListener('resize', resize)` raw, and that
     resize() reallocates a multi-megapixel canvas buffer. On mobile the URL
     bar sliding in and out fires resize in bursts *while you scroll*, so the
     buffer was being thrown away and rebuilt mid-gesture. Debounced, the
     whole burst collapses into one call once the gesture settles.

     Subscribers are also fired when the sticky header changes height, since
     the canvases are anchored below it (see --pv-nav-h).
     --------------------------------------------------------------- */
  var subs = [];
  var lastW = 0, lastH = 0, timer = 0;

  function fire() {
    for (var i = 0; i < subs.length; i++) {
      try { subs[i](); } catch (e) {}
    }
  }

  window.pvOnResize = function (fn) { subs.push(fn); };

  window.addEventListener('resize', function () {
    clearTimeout(timer);
    timer = setTimeout(function () {
      var w = window.innerWidth, h = window.innerHeight;
      if (w === lastW && h === lastH) return;   // spurious event, nothing moved
      lastW = w; lastH = h;
      fire();
    }, 150);
  });

  /* ---------------------------------------------------------------
     --pv-nav-h — the sticky header's height, published to CSS.

     The full-screen canvases are position:fixed and used to sit *under* the
     sticky header, which carries backdrop-filter:blur(10px). Every canvas
     frame dirtied the header's backdrop, so the browser re-ran a gaussian
     blur over that strip 30 times a second, permanently, even on an idle
     page. Anchoring the canvas below the header takes it out of that
     backdrop: the blur now only recomputes when real content scrolls under
     it, which is the part you actually see.

     Measured rather than hard-coded because the header's height moves with
     the viewport, the language switch, and the cart button pv-cart.js
     injects into it.
     --------------------------------------------------------------- */
  function trackNav() {
    var nav = document.querySelector('.nav, .cm-nav');
    if (!nav) return;
    var last = -1;
    function set() {
      var h = nav.offsetHeight;
      if (h === last) return;
      last = h;
      document.documentElement.style.setProperty('--pv-nav-h', h + 'px');
      fire();                    // canvases are sized off this, re-fit them
    }
    set();
    if (window.ResizeObserver) new ResizeObserver(set).observe(nav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackNav);
  } else {
    trackNav();
  }

  window.pvBgLoop = function (drawFrame) {
    // Reduced motion: render a single static frame, never animate.
    if (mq && mq.matches) {
      try { drawFrame(); } catch (e) {}
      return { stop: function () {} };
    }
    var id = 0, last = 0, live = true;
    function step(now) {
      if (!live) return;
      if (paused()) { id = requestAnimationFrame(step); return; }
      if (now - last >= FRAME_MS - 1) {
        last = now;
        try { drawFrame(); } catch (e) {}
      }
      id = requestAnimationFrame(step);
    }
    id = requestAnimationFrame(step);
    return { stop: function () { live = false; if (id) cancelAnimationFrame(id); } };
  };
})();
